import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit, Trash2, Save, Loader2, Send, Play, Pause, X, Clock, FileText, Megaphone, Users, CheckCircle, AlertCircle, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getAllEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  getAllEmailSequences,
  createEmailSequence,
  updateEmailSequence,
  deleteEmailSequence,
  triggerEmailSequence,
  createDefaultSequence,
  getEmailHistory,
  sendBroadcastEmail,
  deleteEmailTemplate
} from '../firebase/email';
import { getAllSubscribers } from '../firebase/subscribers';
import { getStorefrontSettings } from '../firebase/storefront';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';
import { EmailSequenceCard } from './EmailSequenceCard';
import { SimplifiedSequenceEditor } from './SimplifiedSequenceEditor';
import { EmailEditor } from './EmailEditor';

export function AdminEmailTab({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'templates', 'sequences', 'campaigns'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingSequence, setEditingSequence] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showHelp, setShowHelp] = useState(true);
  const [showTemplateHelp, setShowTemplateHelp] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, sequencesData, storefrontData] = await Promise.all([
        getAllEmailTemplates(),
        getAllEmailSequences(),
        getStorefrontSettings()
      ]);
      setTemplates(templatesData);
      setSequences(sequencesData);
      if (storefrontData?.profilePicture) {
        setProfilePicture(storefrontData.profilePicture);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load email data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      let result;
      if (editingTemplate) {
        result = await updateEmailTemplate(editingTemplate.id, templateData);
      } else {
        result = await createEmailTemplate(templateData);
      }

      if (result.success) {
        showToast(`Template ${editingTemplate ? 'updated' : 'created'} successfully`, 'success');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        loadData();
      } else {
        showToast('Error saving template: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving template: ' + error.message, 'error');
    }
  };

  const handleSaveSequence = async (sequenceData) => {
    try {
      let result;
      if (editingSequence) {
        result = await updateEmailSequence(editingSequence.id, sequenceData);
      } else {
        result = await createEmailSequence(sequenceData);
      }

      if (result.success) {
        showToast(`Sequence ${editingSequence ? 'updated' : 'created'} successfully`, 'success');
        setShowSequenceModal(false);
        setEditingSequence(null);
        loadData();
      } else {
        showToast('Error saving sequence: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving sequence: ' + error.message, 'error');
    }
  };

  const handleToggleSequence = async (sequenceId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const result = await updateEmailSequence(sequenceId, { status: newStatus });
      if (result.success) {
        showToast(`Sequence ${newStatus}`, 'success');
        loadData();
      }
    } catch (error) {
      showToast('Error updating sequence', 'error');
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    try {
      const result = await deleteEmailSequence(sequenceId);
      if (result.success) {
        showToast('Sequence deleted successfully', 'success');
        loadData();
      } else {
        showToast('Error deleting sequence: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error deleting sequence: ' + error.message, 'error');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Are you sure you want to delete template "${template.name}"?`)) return;

    try {
      const result = await deleteEmailTemplate(template.id);
      if (result.success) {
        showToast('Template deleted successfully', 'success');
        loadData();
      } else {
        showToast('Error deleting template: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error deleting template: ' + error.message, 'error');
    }
  };

  const handleSendTest = async (sequence) => {
    const email = prompt('Enter recipient email address:');
    if (!email || !email.includes('@')) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    const name = prompt('Enter recipient name (optional):') || 'Customer';

    try {
      const result = await triggerEmailSequence(sequence.trigger, {
        email,
        name,
        variables: {}
      });

      if (result.success) {
        showToast(`Test email sent! ${result.emailsScheduled || 0} email(s) scheduled.`, 'success');
      } else {
        showToast('Error sending test: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error sending test: ' + error.message, 'error');
    }
  };

  const handleDuplicate = async (sequence) => {
    try {
      const duplicatedData = {
        name: `${sequence.name} (Copy)`,
        trigger: sequence.trigger,
        status: 'draft',
        emails: sequence.emails?.map(e => ({ ...e })) || []
      };
      const result = await createEmailSequence(duplicatedData);
      if (result.success) {
        showToast('Sequence duplicated successfully', 'success');
        loadData();
      }
    } catch (error) {
      showToast('Error duplicating sequence: ' + error.message, 'error');
    }
  };

  const handleActivateDefault = async (type) => {
    try {
      const result = await createDefaultSequence(type);
      if (result.success) {
        showToast('Default sequence created! Edit it to customize.', 'success');
        await loadData();
        // Find the newly created sequence and open it
        const updatedSequences = await getAllEmailSequences();
        const newSequence = updatedSequences.find(s => s.id === result.id);
        if (newSequence) {
          setEditingSequence(newSequence);
          setShowSequenceModal(true);
        }
      } else {
        showToast('Error creating sequence: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error creating sequence: ' + error.message, 'error');
    }
  };

  // Pre-built sequence templates
  const defaultSequences = [
    {
      type: 'welcome',
      name: 'Welcome Series',
      description: '3 emails: Welcome, Your Story, Getting Started',
      trigger: 'welcome',
      label: 'When someone subscribes'
    },
    {
      type: 'purchase',
      name: 'Purchase Confirmation',
      description: '1 email: Thank you and order details',
      trigger: 'purchase',
      label: 'When someone buys something'
    },
    {
      type: 'booking-confirmed',
      name: 'Booking Confirmation',
      description: '1 email: Booking details and reminders',
      trigger: 'booking-confirmed',
      label: 'When someone books a session'
    },
    {
      type: 'course-enrolled',
      name: 'Course Welcome',
      description: '2 emails: Welcome and getting started',
      trigger: 'course-enrolled',
      label: 'When someone enrolls in a course'
    },
    {
      type: 'membership-activated',
      name: 'Membership Welcome',
      description: '2 emails: Welcome and benefits overview',
      trigger: 'membership-activated',
      label: 'When someone joins membership'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-[#c5a059]" size={32} />
      </div>
    );
  }

  // Check which default sequences are already created
  const existingTriggers = sequences.map(s => s.trigger);
  // Note: This logic assumes one sequence per trigger type, which is a simplification but works for defaults
  const availableDefaults = defaultSequences.filter(ds => !existingTriggers.includes(ds.type));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">
            {activeView === 'overview' ? 'Automated Emails' : 'Email Management'}
          </h2>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 text-gray-400 hover:text-gray-600 transition rounded-lg hover:bg-gray-50"
            title="Show help"
          >
            <HelpCircle size={20} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('overview')}
              className={`px-4 py-2 rounded-md transition ${activeView === 'overview'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveView('templates')}
              className={`px-4 py-2 rounded-md transition ${activeView === 'templates'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600'
                }`}
            >
              Templates
            </button>
            <button
              onClick={() => setActiveView('sequences')}
              className={`px-4 py-2 rounded-md transition ${activeView === 'sequences'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600'
                }`}
            >
              Sequences
            </button>
            <button
              onClick={() => setActiveView('campaigns')}
              className={`px-4 py-2 rounded-md transition ${activeView === 'campaigns'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600'
                }`}
            >
              Campaigns
            </button>
          </div>
          {activeView !== 'campaigns' && activeView !== 'overview' && (
            <button
              onClick={() => {
                if (activeView === 'templates') {
                  setEditingTemplate(null);
                  setShowTemplateModal(true);
                } else {
                  setEditingSequence(null);
                  setShowSequenceModal(true);
                }
              }}
              className="flex items-center px-4 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              <Plus size={18} className="mr-2" />
              New {activeView === 'templates' ? 'Template' : 'Sequence'}
            </button>
          )}
        </div>
      </div>

      {activeView === 'overview' && (
        <div className="space-y-6">
          {/* Collapsible Help */}
          {showHelp && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-blue-900">How Automated Emails Work</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-blue-800 mb-3">
                Automated emails send automatically when specific events happen (like a purchase or booking).
                You can create email series with multiple emails that send over time.
              </p>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>• Templates:</strong> Reusable email designs you can use in sequences</p>
                <p><strong>• Sequences:</strong> Series of emails that send automatically based on triggers</p>
                <p><strong>• Variables:</strong> Use {'{{name}}'}, {'{{orderId}}'}, {'{{total}}'} to personalize emails</p>
              </div>
            </div>
          )}

          {/* Get Started Section */}
          {availableDefaults.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Get Started</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableDefaults.map(defaultSeq => (
                  <div
                    key={defaultSeq.type}
                    className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                  >
                    <h4 className="font-semibold text-gray-800 mb-2">{defaultSeq.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{defaultSeq.description}</p>
                    <p className="text-xs text-gray-500 mb-4">Trigger: {defaultSeq.label}</p>
                    <button
                      onClick={() => handleActivateDefault(defaultSeq.type)}
                      className="w-full px-4 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition text-sm font-medium"
                    >
                      Activate {defaultSeq.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing Sequences */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Your Email Series</h3>
              <button
                onClick={() => {
                  setEditingSequence(null);
                  setShowSequenceModal(true);
                }}
                className="text-sm text-[#c5a059] hover:text-[#b08e4d] font-medium"
              >
                + Create Custom Series
              </button>
            </div>

            {sequences.length === 0 ? (
              availableDefaults.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">All default email series are set up!</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center border border-dashed border-gray-300">
                  <p className="text-gray-500">Activate a series above or create a custom one.</p>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sequences.map(sequence => (
                  <EmailSequenceCard
                    key={sequence.id}
                    sequence={sequence}
                    templates={templates}
                    onEdit={(seq) => {
                      setEditingSequence(seq);
                      setShowSequenceModal(true);
                    }}
                    onToggle={handleToggleSequence}
                    onSendTest={handleSendTest}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDeleteSequence}
                    showToast={showToast}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'templates' && (
        <div className="space-y-4">
          <FeatureGuide
            title="Email Templates Guide"
            description="Email templates are reusable email designs you can use in sequences or send manually. Create templates for welcome emails, purchase confirmations, newsletters, and more. Use variables to personalize each email."
            steps={[
              "Click 'New Template' to create your first template",
              "Give your template a descriptive name (e.g., 'Welcome Email')",
              "Write a compelling subject line (this is what recipients see first)",
              "Create your email content using the visual editor",
              "Use variables like {{name}} and {{orderId}} for personalization",
              "Save your template to use in sequences or send manually"
            ]}
            tips={[
              "Subject lines should be clear and compelling",
              "Use the visual editor to add images and formatting",
              "Test your templates before using them",
              "Include a clear call-to-action"
            ]}
          />

          {templates.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No email templates yet</p>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setShowTemplateModal(true);
                }}
                className="px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
              >
                Create First Template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <div key={template.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 mb-1 truncate">{template.name || 'Untitled Template'}</h3>
                      <p className="text-sm text-gray-500 truncate">{template.subject || 'No subject'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowTemplateModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-[#c5a059] transition rounded"
                        title="Edit Template"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-gray-400 hover:text-red-500 transition rounded"
                        title="Delete Template"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3 bg-gray-50 p-2 rounded border border-gray-100 italic">
                    {template.textContent ? template.textContent.substring(0, 100) : 'No preview available'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeView === 'sequences' && (
        <div className="space-y-4">
          <FeatureGuide
            title="Email Sequences Guide"
            description="Email sequences are automated email series that send based on triggers. Create welcome sequences, purchase follow-ups, abandoned cart reminders, and nurture sequences."
            steps={[
              "Click 'New Sequence' to create your first sequence",
              "Choose a trigger (Welcome, Purchase, etc.)",
              "Add emails and select templates for each",
              "Set delays between emails",
              "Activate the sequence to start sending"
            ]}
          />
          {sequences.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No email sequences yet</p>
              <button
                onClick={() => {
                  setEditingSequence(null);
                  setShowSequenceModal(true);
                }}
                className="px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
              >
                Create First Sequence
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {sequences.map(sequence => (
                <EmailSequenceCard
                  key={sequence.id}
                  sequence={sequence}
                  templates={templates}
                  onEdit={(seq) => {
                    setEditingSequence(seq);
                    setShowSequenceModal(true);
                  }}
                  onToggle={handleToggleSequence}
                  onSendTest={handleSendTest}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDeleteSequence}
                  showToast={showToast}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Campaigns View */}
      {activeView === 'campaigns' && (
        <CampaignsView
          templates={templates}
          showToast={showToast}
        />
      )}

      {/* Template Editor Modal - Now using Advanced EmailEditor */}
      {showTemplateModal && (
        <TemplateEditorModal
          template={editingTemplate}
          profilePicture={profilePicture}
          onSave={handleSaveTemplate}
          onClose={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}

      {/* Sequence Editor Modal */}
      {showSequenceModal && (
        <SimplifiedSequenceEditor
          sequence={editingSequence}
          templates={templates}
          onSave={handleSaveSequence}
          onClose={() => {
            setShowSequenceModal(false);
            setEditingSequence(null);
          }}
        />
      )}
    </div>
  );
}

// Template Editor Component using Advanced EmailEditor
function TemplateEditorModal({ template, profilePicture, onSave, onClose }) {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [htmlContent, setHtmlContent] = useState(template?.htmlContent || '');

  const handleSave = () => {
    if (!name || !subject || !htmlContent) {
      alert('Please fill in all fields');
      return;
    }
    onSave({
      name,
      subject,
      htmlContent,
      textContent: htmlContent.replace(/<[^>]*>/g, '')
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-none sm:rounded-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            {template ? 'Edit Template' : 'New Template'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 flex-grow overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                placeholder="e.g. Welcome Email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                placeholder="Email subject"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content
              <span className="ml-2 text-xs font-normal text-gray-500">
                (Use variables like {'{{name}}'}, {'{{orderId}}'}, {'{{total}}'}, etc.)
              </span>
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <EmailEditor
                value={htmlContent}
                onChange={setHtmlContent}
                brandName="FreeSpiritFabiha"
                profilePicture={profilePicture}
                showPreview={true}
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
          >
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
}

// Campaigns View Component (Preserved from recent changes)
function CampaignsView({ templates, showToast }) {
  const [composeMode, setComposeMode] = useState('template'); // 'template' or 'compose'
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState(''); // For direct compose mode
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  const [storefrontSettings, setStorefrontSettings] = useState(null);

  useEffect(() => {
    loadSubscriberCount();
    loadStorefrontSettings();
  }, []);

  const loadSubscriberCount = async () => {
    setLoadingSubscribers(true);
    try {
      const subscribers = await getAllSubscribers();
      setSubscriberCount(subscribers.filter(s => s.status === 'active').length);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const loadStorefrontSettings = async () => {
    try {
      const settings = await getStorefrontSettings();
      setStorefrontSettings(settings);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const handleSendCampaign = async () => {
    if (subscriberCount === 0) {
      showToast('No active subscribers to send to', 'error');
      return;
    }

    let payload = {};

    if (composeMode === 'template') {
      if (!selectedTemplateId) {
        showToast('Please select a template', 'error');
        return;
      }
      payload = {
        templateId: selectedTemplateId,
        customSubject: customSubject || undefined
      };
    } else {
      if (!customSubject) {
        showToast('Please enter a subject line', 'error');
        return;
      }
      if (!htmlContent || htmlContent.length < 20) {
        showToast('Please write some content for your email', 'error');
        return;
      }
      payload = {
        subject: customSubject,
        htmlContent: htmlContent
      };
    }

    if (!confirm(`Send this email to ${subscriberCount} active subscriber${subscriberCount !== 1 ? 's' : ''}?`)) {
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await sendBroadcastEmail(payload);

      setResult(response);

      if (response.success) {
        showToast(`Campaign sent! ${response.sent} emails delivered`, 'success');
        // Reset form if sent successfully
        if (composeMode === 'compose') {
          setCustomSubject('');
          setHtmlContent('');
        }
      } else {
        showToast('Campaign failed: ' + response.error, 'error');
      }
    } catch (error) {
      showToast('Error sending campaign: ' + error.message, 'error');
      setResult({ success: false, error: error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <FeatureGuide
        title="Email Campaigns"
        description="Send one-time broadcast emails to all your active subscribers. You can use a pre-made template or write a new email from scratch."
        steps={[
          "Choose 'Select Template' to use a saved design, or 'Write New' to start fresh",
          "For templates: Select one from the dropdown and optionally override the subject",
          "For new emails: Write your subject and content using the visual editor",
          "Review the subscriber count",
          "Click 'Send Campaign' to broadcast to all active subscribers"
        ]}
        tips={[
          "Use 'Write New' for quick updates or newsletters",
          "Templates are better for recurring emails (like monthly updates)",
          "Personalization variables like {{name}} work in both modes",
          "Emails are sent in batches to ensure high deliverability"
        ]}
      />

      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#fae1e5] flex items-center justify-center">
              <Megaphone className="text-[#c5a059]" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Send Campaign</h3>
              <p className="text-sm text-gray-500">Broadcast to all active subscribers</p>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 self-start md:self-auto">
            <button
              onClick={() => setComposeMode('template')}
              className={`px-4 py-2 rounded-md transition text-sm font-medium ${composeMode === 'template'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Select Template
            </button>
            <button
              onClick={() => setComposeMode('compose')}
              className={`px-4 py-2 rounded-md transition text-sm font-medium ${composeMode === 'compose'
                ? 'bg-white text-[#c5a059] shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Write New
            </button>
          </div>
        </div>

        {/* Subscriber Count Information */}
        <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-[#fae1e5]/30 to-[#f3e6d5]/30 rounded-lg border border-[#eaddcf]">
          <Users className="text-[#c5a059]" size={24} />
          <div>
            <p className="text-sm text-gray-600">Active Subscribers</p>
            <p className="text-2xl font-semibold text-gray-800">
              {loadingSubscribers ? (
                <Loader2 className="animate-spin inline" size={20} />
              ) : (
                subscriberCount
              )}
            </p>
          </div>
        </div>

        {composeMode === 'template' ? (
          /* TEMPLATE MODE */
          <div className="space-y-6 animate-in fade-in duration-300">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              >
                <option value="">Choose a template...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} - {template.subject}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Subject Line (optional)
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder={selectedTemplate?.subject || "Override the template subject..."}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to use the template's subject line</p>
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Subject:</strong> {customSubject || selectedTemplate.subject}
                </p>
                <p className="text-sm text-gray-500 line-clamp-3">
                  {selectedTemplate.htmlContent?.replace(/<[^>]*>/g, '').substring(0, 200)}...
                </p>
              </div>
            )}
          </div>
        ) : (
          /* DIRECT COMPOSE MODE */
          <div className="space-y-6 animate-in fade-in duration-300">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject Line <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="e.g. Exciting news! 🌟"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Content <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <EmailEditor
                  value={htmlContent}
                  onChange={setHtmlContent}
                  brandName="FreeSpiritFabiha"
                  profilePicture={storefrontSettings?.profilePicture}
                  showPreview={true}
                />
              </div>
            </div>
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleSendCampaign}
          disabled={sending || subscriberCount === 0 || (composeMode === 'template' && !selectedTemplateId) || (composeMode === 'compose' && (!customSubject || !htmlContent))}
          className="w-full py-3 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Sending Campaign...
            </>
          ) : (
            <>
              <Send size={18} />
              Send Campaign to {subscriberCount} Subscriber{subscriberCount !== 1 ? 's' : ''}
            </>
          )}
        </button>

        {result && (
          <div className={`p-4 rounded-lg flex items-start gap-3 ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {result.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <div>
              <p className="font-medium">{result.success ? 'Campaign Sent Successfully' : 'Campaign Failed'}</p>
              <p className="text-sm mt-1">{result.success ? `Successfully queued ${result.sent} emails for delivery.` : result.error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
